import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { centerCostAPI } from '../api/client';
import { Page, PageHeader, Button, EmptyState } from '../components/ui';
import { COLORS, TONES, ICON_STROKE, input as uiInput, btn } from '../utils/uiStyles';
import { IconReportMoney, IconBuildingWarehouse, IconCalendar } from '@tabler/icons-react';
import { kstToday, kstDaysAgo } from '../utils/kstTime';

/* 센터 비용 통합조회 (마스터 2026-08-18)
 * 각 센터가 올린 자료를 한 화면에서 본다. 아직 금액을 못 읽으므로(양식이 제각각)
 * **제출 현황(일자 × 센터)** 을 보여 준다 — 이 화면의 실제 쓸모는 "어느 센터가 안 올렸나"다.
 * 파싱이 붙으면 이 표의 셀을 건수 대신 금액으로 바꾸면 된다.
 */
// 🔴 iso() 는 **기간 목록 루프용**이다(날짜만 있는 문자열을 UTC 자정으로 읽어 UTC 로 되돌린다 = 왕복 정확).
//    반면 «오늘»은 UTC 로 만들면 KST 00~09시에 하루 어긋난다 → kstToday/kstDaysAgo 를 쓴다(2026-10-03).
const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => kstDaysAgo(n);

export default function CenterCostAllPage() {
  const navigate = useNavigate();
  const [start, setStart] = useState(daysAgo(13));
  const [end, setEnd] = useState(kstToday());
  const [data, setData] = useState({ centers: [], dates: [], cells: {} });
  const [loading, setLoading] = useState(true);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    centerCostAPI.summary(start, end)
      .then((r) => setData(r.data || { centers: [], dates: [], cells: {} }))
      .catch(() => setData({ centers: [], dates: [], cells: {} }))
      .finally(() => setLoading(false));
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  // 기간 안의 모든 날짜(제출이 하나도 없는 날도 줄로 남겨야 '안 올린 날'이 보인다)
  const allDates = (() => {
    const out = [];
    try {
      const s = new Date(start), e = new Date(end);
      for (let d = new Date(e); d >= s && out.length < 120; d.setDate(d.getDate() - 1)) out.push(iso(d));
    } catch (err) { /* 잘못된 날짜 입력 */ }
    return out;
  })();
  const centers = data.centers || [];
  const cell = (tok, ds) => (data.cells || {})[`${tok}|${ds}`] || 0;
  const rows = onlyMissing ? allDates.filter((ds) => centers.some((c) => !cell(c.token, ds))) : allDates;

  const input = { ...uiInput, width: 'auto' };
  const th = { textAlign: 'left', height: '33px', padding: '5px 9px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textMuted, fontWeight: 400, fontSize: '12px', whiteSpace: 'nowrap' };
  // 첫 열(일자) — 값은 th/td 와 같고 왼쪽 패딩만 4(시안 .database :first-child). shorthand 와 paddingLeft 를 한 객체에 섞지 않는다.
  const thFirst = { textAlign: 'left', height: '33px', paddingTop: '5px', paddingRight: '9px', paddingBottom: '5px', paddingLeft: '4px', borderBottom: `1px solid ${COLORS.borderSoft}`, color: COLORS.textMuted, fontWeight: 400, fontSize: '12px', whiteSpace: 'nowrap' };
  const td = { height: '41px', padding: '7px 9px', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', color: COLORS.textSub, verticalAlign: 'middle', wordBreak: 'break-word', textAlign: 'left' };
  const tdFirst = { height: '41px', paddingTop: '7px', paddingRight: '9px', paddingBottom: '7px', paddingLeft: '4px', borderBottom: `1px solid ${COLORS.borderSoft}`, fontSize: '13px', color: COLORS.text, fontWeight: 500, verticalAlign: 'middle', wordBreak: 'break-word', textAlign: 'left' };
  // 가로로 밀어도 일자 열은 남는다 — 상태색 칸 위로 겹치므로 불투명 바탕 필수
  const stickyFirst = { position: 'sticky', left: 0, background: COLORS.surface, zIndex: 1 };
  const colIcon = { marginRight: '5px', verticalAlign: 'middle' };

  return (
    <AppShell app="center-all">
      {/* 본문은 사이드바의 형제다 — AppSidebar 의 children 은 사이드바 아코디언용이라
          거기에 넣으면 화면이 통째로 비어 보인다(레포 규약: DirectoryPage 참조). */}
      <div style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto' }}>
      <Page>
        <PageHeader
          icon={<IconReportMoney aria-hidden="true" />}
          title="센터 비용 통합조회"
          subtitle="각 센터가 올린 일일 비용자료(소모품비·인건비·운송료·보관비)의 제출 현황입니다. 숫자는 그날 올라온 건수이고, 빈칸은 미제출입니다. 센터명을 누르면 그 센터 자료로 이동합니다. 금액 합계는 양식 표준화·파싱이 붙은 뒤에 이 표에 채워집니다."
        />

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <span style={{ fontSize: '13px', color: COLORS.textSub }}>기간</span>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} style={input} />
          <span style={{ color: COLORS.textMuted }}>~</span>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={input} />
          <Button variant="secondary" onClick={() => { setStart(daysAgo(13)); setEnd(kstToday()); }}>최근 2주</Button>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', color: COLORS.text }}>
            <input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} />
            미제출이 있는 날만
          </label>
          <span style={{ fontSize: '12px', color: COLORS.textMuted }}>센터 {centers.length}곳 · {rows.length}일</span>
        </div>

        {loading ? (
          <div style={{ padding: '30px', color: COLORS.textMuted, fontSize: '14px' }}>불러오는 중…</div>
        ) : centers.length === 0 ? (
          <EmptyState description="조회할 수 있는 센터가 없습니다." />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: `${140 + centers.length * 104}px` }}>
              <colgroup>
                <col style={{ width: '140px' }} />
                {centers.map((c) => <col key={c.token} style={{ width: '104px' }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th style={{ ...thFirst, ...stickyFirst }}>
                    <IconCalendar size={14} stroke={ICON_STROKE} aria-hidden="true" style={colIcon} />일자
                  </th>
                  {centers.map((c) => (
                    <th key={c.token} style={th}>
                      <IconBuildingWarehouse size={14} stroke={ICON_STROKE} aria-hidden="true" style={colIcon} />
                      <button onClick={() => navigate(`/center/${encodeURIComponent(c.token)}`)}
                        title={`${c.name} 자료로 이동`}
                        style={{ ...btn('quiet'), padding: 0, color: COLORS.primaryDark, fontWeight: 500, fontSize: '12px' }}>
                        {c.token}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((ds) => (
                  <tr key={ds} className="gw-tr">
                    <td style={{ ...tdFirst, whiteSpace: 'nowrap', ...stickyFirst }}>{ds}</td>
                    {centers.map((c) => {
                      const n = cell(c.token, ds);
                      return (
                        <td key={c.token} style={{ ...td, background: n ? TONES.green.soft : TONES.orange.soft, color: n ? TONES.green.ink : COLORS.textSub, fontWeight: n ? 500 : 400 }}
                          title={`${c.name} · ${ds} — ${n ? `${n}건 제출` : '미제출'}`}>
                          {n || '—'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Page>
      </div>
    </AppShell>
  );
}
